import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  ConfigEntry,
  VaultParams,
  getVaultParams,
} from 'src/pages/admin/services/vaultParamsService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import { VAULT_PARAMS_ERROR, VAULT_PARAMS_NS as NS } from './constants';
import { FieldSpec } from './fields';
import {
  Draft,
  FieldChange,
  FieldErrors,
  diffFields,
  fieldUnits,
  fromDraft,
  loadErrorMessage,
  saveErrorMessages,
  toDraft,
} from './helpers';

/** Loads `GET /vault/params` and keeps the latest server truth. */
export const useVaultParams = () => {
  const translate = useTranslate();
  const [params, setParams] = useState<VaultParams | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // HTTP status of the last failed load (null after a success), so a reader can tell a
  // missing permission (403) from an engine failure.
  const [loadErrorStatus, setLoadErrorStatus] = useState<number | null>(null);

  // `translate` from react-admin is not identity-stable across renders. Reading
  // it through a ref (rather than a `useCallback` dependency) keeps `refetch`
  // itself stable, so the mount effect below fires once instead of looping.
  const translateRef = useRef(translate);
  translateRef.current = translate;

  const refetch = useCallback(async (): Promise<VaultParams | null> => {
    setLoading(true);
    try {
      const fresh = await getVaultParams();
      setParams(fresh);
      setLoadError(null);
      setLoadErrorStatus(null);
      return fresh;
    } catch (error) {
      const info = parseApiError(error);
      setLoadError(loadErrorMessage(info, translateRef.current));
      setLoadErrorStatus(info.status ?? null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { params, loading, loadError, loadErrorStatus, refetch };
};

export interface EditorOptions<T> {
  entry: ConfigEntry<T>;
  /** The engine default, used to pre-fill a key that was never saved. */
  defaults: T | null;
  fields: FieldSpec<T>[];
  validate: (draft: Draft) => FieldErrors;
  save: (value: T, expectedRevision: number) => Promise<unknown>;
  refetch: () => Promise<VaultParams | null>;
  revisionOf: (params: VaultParams) => number;
}

/**
 * The card banner after a failed save:
 * - `conflict`: someone else saved first (CAS); the latest values are shown.
 * - `maybeApplied`: the save failed without a conflict, yet the revision moved
 *   (e.g. the response was lost after the engine committed); check the values.
 */
export type SaveNotice = 'conflict' | 'maybeApplied';

/**
 * edit → confirm → save for one single-value key. Every failed save re-reads,
 * so the card never shows stale values:
 * - 409 VAULT_PARAMS_CONFLICT is a conflict on its own.
 * - An older BFF sends 422 for both a conflict and a rejected value; there,
 *   as in fee-config's runCasSave, only the re-read revision tells them apart.
 * - Any other failure after which the revision moved may have been applied.
 */
export const useParamsEditor = <T>({
  entry,
  defaults,
  fields,
  validate,
  save,
  refetch,
  revisionOf,
}: EditorOptions<T>) => {
  const translate = useTranslate();
  const notify = useNotify();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [draft, setDraft] = useState<Draft>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [changes, setChanges] = useState<FieldChange[]>([]);
  const [notice, setNotice] = useState<SaveNotice | null>(null);
  const [saving, setSaving] = useState(false);

  const entryRef = useRef(entry);
  entryRef.current = entry;

  const baseline = useCallback((): T | null => entryRef.current.value ?? defaults, [defaults]);

  const openDialog = useCallback(() => {
    const start = baseline();
    setDraft(start ? toDraft(start, fields) : {});
    setErrors({});
    setFormErrors([]);
    setChanges([]);
    setStep('edit');
    setOpen(true);
  }, [baseline, fields]);

  const closeDialog = useCallback(() => setOpen(false), []);

  const setField = useCallback((key: string, value: string | string[]) => {
    setDraft((current) => ({ ...current, [key]: value }));
  }, []);

  const review = useCallback(() => {
    const nextErrors = validate(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors([translate(`${NS}.errors.fixFields`)]);
      return;
    }
    // Diff against what is STORED (null when never saved), not the default:
    // a first save writes every field, and the confirm step says so.
    const next = diffFields(
      entryRef.current.value,
      fromDraft<T>(draft, fields),
      fields,
      fieldUnits(translate)
    );
    if (next.length === 0) {
      setFormErrors([translate(`${NS}.noChanges`)]);
      return;
    }
    setFormErrors([]);
    setChanges(next);
    setStep('confirm');
  }, [draft, fields, translate, validate]);

  const back = useCallback(() => setStep('edit'), []);

  const confirm = useCallback(async () => {
    const expectedRevision = entryRef.current.revision;
    setSaving(true);
    try {
      await save(fromDraft<T>(draft, fields), expectedRevision);
      await refetch();
      setNotice(null);
      setOpen(false);
      notify(translate(`${NS}.updated`), { type: 'success' });
    } catch (error) {
      const info = parseApiError(error);
      const fresh = await refetch();
      const moved = fresh !== null && revisionOf(fresh) !== expectedRevision;
      // Refused before anything was written: the BFF's DTO check or the
      // engine's value check. Even if the revision moved meanwhile, this save
      // was not applied — keep the dialog open with the reason.
      const refused = info.status === 400 || info.code === VAULT_PARAMS_ERROR.INVALID;
      const isConflict =
        info.status === 409 ||
        info.code === VAULT_PARAMS_ERROR.CONFLICT ||
        (moved && info.status === 422 && !refused);
      if (isConflict || (moved && !refused)) {
        const next: SaveNotice = isConflict ? 'conflict' : 'maybeApplied';
        setNotice(next);
        setOpen(false);
        notify(
          translate(`${NS}.${next === 'conflict' ? 'conflictNotify' : 'maybeAppliedNotify'}`),
          {
            type: 'warning',
          }
        );
        return;
      }
      setStep('edit');
      setFormErrors(saveErrorMessages(info, translate));
    } finally {
      setSaving(false);
    }
  }, [draft, fields, notify, refetch, revisionOf, save, translate]);

  return {
    open,
    step,
    draft,
    errors,
    formErrors,
    changes,
    notice,
    saving,
    openDialog,
    closeDialog,
    setField,
    review,
    back,
    confirm,
  };
};

/**
 * Stable name for the editor's return shape. Instantiation expressions
 * (`typeof useParamsEditor<T>`, TS 4.7+) still need a generic alias like this
 * one for consumers to import.
 */
export type ParamsEditor<T> = ReturnType<typeof useParamsEditor<T>>;
